import { useBulkFetchTimelineItems } from '@/features/planner/queries/useBulkFetchTimelineItems';
import type { PlanSchedulingScenarioContext } from '../adapters/types';
import { useSchedulesQueryWithPublished } from './useSchedulesQueryWithPublished';

interface ScheduleBarData {
  forecastedStart: string | undefined;
  forecastedFinish: string | undefined;
  latestFinish: string | undefined;
  deadline: string | undefined;
  isLoading: boolean;
}

export const useScheduleBarData = (
  itemId: number,
  scenarioContext?: PlanSchedulingScenarioContext,
  enabled = true,
): ScheduleBarData => {
  const { data: timelineItems, isLoading: isTimelineLoading } = useBulkFetchTimelineItems({
    loopId: scenarioContext?.loopId ?? 0,
    scenarioId: scenarioContext?.scenarioId ?? 0,
    itemIds: scenarioContext ? [itemId] : [],
    enabled,
  });

  const { data: schedulesData, isLoading: isScheduleLoading } = useSchedulesQueryWithPublished(
    itemId,
    enabled && !scenarioContext,
  );

  if (!enabled) {
    return {
      forecastedStart: undefined,
      forecastedFinish: undefined,
      latestFinish: undefined,
      deadline: undefined,
      isLoading: false,
    };
  }

  if (scenarioContext) {
    const timelineItem = timelineItems?.find((t) => t.item.itemId === itemId);
    const metrics = timelineItem?.scenarioItemMetrics;
    return {
      forecastedStart: metrics?.forecastedStart ?? undefined,
      forecastedFinish: metrics?.forecastedFinish ?? undefined,
      latestFinish: metrics?.latestFinishDate ?? undefined,
      deadline: timelineItem?.scenarioItem.deadlineDateTime ?? undefined,
      isLoading: isTimelineLoading,
    };
  }

  const schedule = schedulesData?.[itemId];
  return {
    forecastedStart: schedule?.forecastedStartDateTime ?? undefined,
    forecastedFinish: schedule?.forecastedFinishDateTime ?? undefined,
    latestFinish: schedule?.latestFinishDateTime ?? undefined,
    deadline: undefined,
    isLoading: isScheduleLoading,
  };
};
